import type { DashboardMode } from '@/types'

interface SummaryCardsProps {
  total: number
  done: number
  pending: number
  percentage: number
  mode: DashboardMode
}

export function SummaryCards({ total, done, pending, percentage, mode }: SummaryCardsProps) {
  const doneLabel = mode === 'production' ? 'Prontos' : 'Agendados'
  const pendingLabel = mode === 'production' ? 'Pendentes' : 'Faltam agendar'

  const statusColor =
    percentage >= 80
      ? 'text-green-600'
      : percentage >= 50
      ? 'text-yellow-600'
      : 'text-red-500'

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
      <SummaryCard
        label="Total de conteúdos"
        value={total}
        icon={<GridIcon />}
        accent="brand-taupe"
      />
      <SummaryCard
        label={doneLabel}
        value={done}
        icon={<CheckIcon />}
        accent="green"
      />
      <SummaryCard
        label={pendingLabel}
        value={pending}
        icon={<ClockIcon />}
        accent="red"
      />
      <div className="card p-5 flex flex-col justify-between">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-medium text-brand-taupe uppercase tracking-wider">
            % {mode === 'production' ? 'Produção' : 'Agendamento'}
          </span>
          <div className="w-8 h-8 rounded-xl bg-brand-beige-light flex items-center justify-center">
            <PercentIcon />
          </div>
        </div>
        <p className={`text-3xl font-semibold ${statusColor}`}>{percentage}%</p>
        <div className="progress-bar-track mt-3">
          <div
            className={`progress-bar-fill ${
              percentage >= 80
                ? 'bg-green-500'
                : percentage >= 50
                ? 'bg-yellow-400'
                : 'bg-red-400'
            }`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
    </div>
  )
}

interface SummaryCardProps {
  label: string
  value: number
  icon: React.ReactNode
  accent: string
}

function SummaryCard({ label, value, icon }: SummaryCardProps) {
  return (
    <div className="card p-5 flex flex-col justify-between">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-medium text-brand-taupe uppercase tracking-wider">
          {label}
        </span>
        <div className="w-8 h-8 rounded-xl bg-brand-beige-light flex items-center justify-center text-brand-taupe">
          {icon}
        </div>
      </div>
      <p className="text-3xl font-semibold text-brand-black">{value}</p>
    </div>
  )
}

function GridIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  )
}

function ClockIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 3" />
    </svg>
  )
}

function PercentIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="9" cy="9" r="2" />
      <circle cx="15" cy="15" r="2" />
      <path d="M16 8L8 16" />
    </svg>
  )
}
